/**
 * Routing Service - Fleet Premium
 * Servico de roteirizacao profissional com OSRM, VROOM e Nominatim
 *
 * As configuracoes sao carregadas dinamicamente da API /api/routing/config
 * que le os valores do traccar.xml (ConfigKeys)
 */

// Cache de configuracao carregada da API
let routingConfig = null;
let configPromise = null;

// Cache de geocoding com TTL (evita requisicoes duplicadas ao Nominatim)
const GEOCODE_CACHE_TTL_MS = 3600_000; // 1 hora
const geocodeCache = new Map();

const getCached = (key) => {
  const entry = geocodeCache.get(key);
  if (entry && Date.now() - entry.ts < GEOCODE_CACHE_TTL_MS) {
    return entry.data;
  }
  geocodeCache.delete(key);
  return null;
};

const setCache = (key, data) => {
  // Limitar tamanho do cache (evitar memory leak)
  if (geocodeCache.size > 500) {
    const oldest = geocodeCache.keys().next().value;
    geocodeCache.delete(oldest);
  }
  geocodeCache.set(key, { data, ts: Date.now() });
};

// Detecta se estamos em desenvolvimento (Vite dev server)
const isDevelopment = import.meta.env.DEV;

// Timeout para requisicoes a servicos externos (ms)
const FETCH_TIMEOUT_MS = 30000;

/**
 * fetch com timeout via AbortController
 */
const fetchWithTimeout = (url, options = {}, timeoutMs = FETCH_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

/**
 * Trata erros HTTP com mensagens especificas por status code
 */
const handleHttpError = (serviceName, response) => {
  const { status } = response;
  if (status === 429) {
    throw new Error(`${serviceName}: Rate limit exceeded — try again later`);
  }
  if (status === 400) {
    throw new Error(`${serviceName}: Bad request — check input parameters`);
  }
  if (status >= 500) {
    throw new Error(`${serviceName}: Server error (${status}) — service may be down`);
  }
  throw new Error(`${serviceName}: HTTP ${status} ${response.statusText}`);
};

// URLs de proxy para desenvolvimento (evita CORS e problemas de rede)
// Os proxies sao configurados em vite.config.js
// Usando servicos publicos como fallback:
// - OSRM: router.project-osrm.org (demo, limite de requests)
// - Nominatim: nominatim.openstreetmap.org (OSM oficial)
const PROXY_URLS = {
  osrmCar: '/routing-osrm',
  osrmBike: '/routing-osrm',
  osrmFoot: '/routing-osrm',
  vroom: '/routing-vroom',
  nominatimReverse: '/routing-nominatim-reverse',
  nominatimSearch: '/routing-nominatim-search',
};

/**
 * Carrega configuracao de routing da API
 * @returns {Promise<Object>} Configuracao de routing
 */
const loadConfig = async () => {
  if (routingConfig) {
    return routingConfig;
  }

  // Evita multiplas requisicoes simultaneas
  if (configPromise) {
    return configPromise;
  }

  configPromise = (async () => {
    try {
      const response = await fetch('/api/routing/config');
      let data = { enabled: false };

      if (response.ok) {
        data = await response.json();
      }

      // Em desenvolvimento, usar proxies do Vite mesmo se o backend nao tiver routing habilitado
      if (isDevelopment) {
        routingConfig = {
          osrm: {
            baseUrl: PROXY_URLS.osrmCar,
            bikeUrl: PROXY_URLS.osrmBike,
            footUrl: PROXY_URLS.osrmFoot,
            profiles: {
              car: 'driving',
              bike: 'cycling',
              foot: 'walking',
            },
          },
          vroom: {
            baseUrl: PROXY_URLS.vroom,
          },
          nominatim: {
            baseUrl: PROXY_URLS.nominatimReverse,
            searchUrl: PROXY_URLS.nominatimSearch,
          },
          defaults: data.defaults || { capacity: 100, maxDeliveries: 100 },
        };

        return routingConfig;
      }

      // Em producao, verificar se routing esta habilitado
      if (!data.enabled) {
        throw new Error('Routing services are not enabled on this server');
      }

      routingConfig = {
        osrm: {
          baseUrl: data.osrm?.car || null,
          bikeUrl: data.osrm?.bike || null,
          footUrl: data.osrm?.foot || null,
          profiles: {
            car: 'driving',
            bike: 'cycling',
            foot: 'walking',
          },
        },
        vroom: {
          baseUrl: data.vroom || null,
        },
        nominatim: {
          baseUrl: data.nominatim || null,
          searchUrl: data.nominatim || null,
        },
        defaults: data.defaults || { capacity: 100, maxDeliveries: 100 },
      };


      return routingConfig;
    } catch (error) {
      // Em desenvolvimento, usar fallback mesmo com erro
      if (isDevelopment) {
        routingConfig = {
          osrm: {
            baseUrl: PROXY_URLS.osrmCar,
            bikeUrl: PROXY_URLS.osrmBike,
            footUrl: PROXY_URLS.osrmFoot,
            profiles: {
              car: 'driving',
              bike: 'cycling',
              foot: 'walking',
            },
          },
          vroom: {
            baseUrl: PROXY_URLS.vroom,
          },
          nominatim: {
            baseUrl: PROXY_URLS.nominatimReverse,
            searchUrl: PROXY_URLS.nominatimSearch,
          },
          defaults: { capacity: 100, maxDeliveries: 100 },
        };

        console.warn('[RoutingService] Config failed, using dev proxies fallback:', error.message);
        return routingConfig;
      }

      configPromise = null;
      throw error;
    }
  })();

  return configPromise;
};

/**
 * Retorna a URL do OSRM para o perfil especificado
 * @param {string} profile - Perfil (car, bike, foot)
 * @returns {string} URL base do OSRM
 */
const getOsrmUrl = (profile = 'car') => {
  if (!routingConfig?.osrm) {
    throw new Error('Routing config not loaded. Call loadConfig() first.');
  }

  switch (profile) {
    case 'bike':
      return routingConfig.osrm.bikeUrl || routingConfig.osrm.baseUrl;
    case 'foot':
      return routingConfig.osrm.footUrl || routingConfig.osrm.baseUrl;
    default:
      return routingConfig.osrm.baseUrl;
  }
};

/**
 * Verifica se o servico de routing esta configurado e disponivel
 * @returns {Promise<boolean>} True se disponivel
 */
export const isRoutingAvailable = async () => {
  try {
    const config = await loadConfig();
    return config.osrm.baseUrl != null && config.vroom.baseUrl != null;
  } catch {
    return false;
  }
};

/**
 * Obtem a configuracao de routing (carrega se necessario)
 * @returns {Promise<Object>} Configuracao
 */
export const getRoutingConfig = async () => loadConfig();

/**
 * Limpa o cache de configuracao (util para reload)
 */
export const clearConfigCache = () => {
  routingConfig = null;
  configPromise = null;
};

// ============================================================================
// OSRM - Roteamento
// ============================================================================

/**
 * Calcula rota entre dois ou mais pontos
 * @param {Array<{lat: number, lng: number}>} coordinates - Array de coordenadas
 * @param {string} profile - Perfil de roteamento (car, bike, foot)
 * @param {Object} options - Opcoes adicionais
 * @returns {Promise<Object>} Resultado da rota
 */
export const calculateRoute = async (coordinates, profile = 'car', options = {}) => {
  await loadConfig();

  const { overview = 'full', geometries = 'geojson', steps = true } = options;

  const osrmUrl = getOsrmUrl(profile);
  if (!osrmUrl) {
    throw new Error('OSRM URL not configured');
  }

  const osrmProfile = routingConfig.osrm.profiles[profile] || 'driving';
  const coordsString = coordinates.map((c) => `${c.lng},${c.lat}`).join(';');

  const url = `${osrmUrl}/route/v1/${osrmProfile}/${coordsString}?overview=${overview}&geometries=${geometries}&steps=${steps}`;

  const response = await fetchWithTimeout(url);
  if (!response.ok) {
    handleHttpError('OSRM route', response);
  }

  const data = await response.json();

  if (data.code !== 'Ok') {
    throw new Error(`OSRM routing failed: ${data.message || data.code}`);
  }

  const route = data.routes[0];

  return {
    distance: route.distance, // metros
    duration: route.duration, // segundos
    geometry: route.geometry, // GeoJSON LineString
    legs: route.legs,
    waypoints: data.waypoints,
  };
};

/**
 * Otimiza ordem de visitas usando TSP (Traveling Salesman Problem)
 * Usa o endpoint nativo /trip do OSRM para otimizacao precisa
 * @param {Array<{lat: number, lng: number, id?: string}>} waypoints - Pontos a visitar
 * @param {Object} options - Opcoes de otimizacao
 * @param {string} options.profile - Perfil de roteamento (car, bike, foot)
 * @param {boolean} options.roundtrip - Se deve retornar ao ponto inicial (default: false)
 * @param {string} options.source - Qual ponto e a origem: 'first', 'last', 'any' (default: 'first')
 * @param {string} options.destination - Qual ponto e o destino: 'first', 'last', 'any' (default: 'last')
 * @returns {Promise<Object>} Resultado da otimizacao com ordem e rota
 */
export const optimizeTSP = async (waypoints, options = {}) => {
  await loadConfig();

  const {
    profile = 'car',
    roundtrip = false,
    source = 'first',
    destination = 'last',
    geometries = 'geojson',
    overview = 'full',
  } = options;

  const osrmUrl = getOsrmUrl(profile);
  if (!osrmUrl) {
    throw new Error('OSRM URL not configured');
  }

  if (waypoints.length < 2) {
    throw new Error('TSP requires at least 2 waypoints');
  }

  const osrmProfile = routingConfig.osrm.profiles[profile] || 'driving';
  const coordsString = waypoints.map((w) => `${w.lng},${w.lat}`).join(';');

  const params = new URLSearchParams({
    roundtrip: roundtrip.toString(),
    source,
    destination,
    geometries,
    overview,
    steps: 'true',
    annotations: 'true',
  });

  const url = `${osrmUrl}/trip/v1/${osrmProfile}/${coordsString}?${params}`;


  const response = await fetchWithTimeout(url);
  if (!response.ok) {
    handleHttpError('OSRM TSP', response);
  }

  const data = await response.json();

  if (data.code !== 'Ok') {
    throw new Error(`OSRM TSP failed: ${data.message || data.code}`);
  }

  // Extrair a melhor rota (OSRM pode retornar multiplas trips)
  const trip = data.trips[0];

  // Mapear waypoints para a ordem otimizada
  // data.waypoints contem o indice original de cada waypoint na ordem otimizada
  const orderedWaypoints = data.waypoints.map((wp, tripIndex) => ({
    ...waypoints[wp.waypoint_index],
    originalIndex: wp.waypoint_index,
    tripIndex,
    snappedLocation: { lng: wp.location[0], lat: wp.location[1] },
  }));

  return {
    distance: trip.distance, // metros
    duration: trip.duration, // segundos
    geometry: trip.geometry, // GeoJSON LineString
    legs: trip.legs,
    // Waypoints na ordem otimizada
    orderedWaypoints,
    // Mapa de indice original -> indice otimizado
    orderMapping: orderedWaypoints.reduce((map, wp, idx) => {
      map[wp.originalIndex] = idx;
      return map;
    }, {}),
    // Ordem original dos indices
    optimizedOrder: orderedWaypoints.map((w) => w.originalIndex),
  };
};

/**
 * Calcula matriz de distancias entre multiplos pontos
 * @param {Array<{lat: number, lng: number}>} coordinates - Array de coordenadas
 * @param {string} profile - Perfil de roteamento
 * @returns {Promise<Object>} Matriz de distancias e tempos
 */
export const calculateMatrix = async (coordinates, profile = 'car') => {
  await loadConfig();

  const osrmUrl = getOsrmUrl(profile);
  if (!osrmUrl) {
    throw new Error('OSRM URL not configured');
  }

  const osrmProfile = routingConfig.osrm.profiles[profile] || 'driving';
  const coordsString = coordinates.map((c) => `${c.lng},${c.lat}`).join(';');

  const url = `${osrmUrl}/table/v1/${osrmProfile}/${coordsString}?annotations=distance,duration`;

  const response = await fetchWithTimeout(url);
  if (!response.ok) {
    handleHttpError('OSRM matrix', response);
  }

  const data = await response.json();

  if (data.code !== 'Ok') {
    throw new Error(`OSRM matrix failed: ${data.message || data.code}`);
  }

  return {
    distances: data.distances, // matriz NxN em metros
    durations: data.durations, // matriz NxN em segundos
    sources: data.sources,
    destinations: data.destinations,
  };
};

// ============================================================================
// VROOM - Otimizacao VRP
// ============================================================================

/**
 * Otimiza rotas para multiplos veiculos e entregas
 * @param {Object} params - Parametros de otimizacao
 * @param {Array} params.vehicles - Lista de veiculos
 * @param {Array} params.jobs - Lista de entregas/servicos
 * @param {Array} params.shipments - Lista de coletas/entregas (opcional)
 * @param {Object} params.options - Opcoes adicionais
 * @returns {Promise<Object>} Resultado da otimizacao
 */
export const optimizeRoutes = async ({ vehicles, jobs, shipments, options = {} }) => {
  await loadConfig();

  if (!routingConfig.vroom.baseUrl) {
    throw new Error('VROOM URL not configured');
  }

  // Input validation
  const maxJobs = routingConfig.defaults?.maxDeliveries || 500;
  if (!vehicles || vehicles.length === 0) {
    throw new Error('At least one vehicle is required');
  }
  if (!jobs || jobs.length === 0) {
    throw new Error('At least one job is required');
  }
  if (jobs.length > maxJobs) {
    throw new Error(`Too many jobs: ${jobs.length} (max ${maxJobs})`);
  }
  for (const j of jobs) {
    const lat = j.lat;
    const lng = j.lng;
    if (lat == null || lng == null || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      throw new Error(`Invalid coordinates for job ${j.id}: lat=${lat}, lng=${lng}`);
    }
  }
  for (const v of vehicles) {
    if (v.capacity != null && v.capacity < 0) {
      throw new Error(`Invalid capacity for vehicle ${v.id}: ${v.capacity}`);
    }
  }

  // Helper para remover campos undefined/null
  const clean = (obj) => {
    const result = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        result[key] = value;
      }
    });
    return result;
  };

  const request = {
    vehicles: vehicles.map((v) => clean({
      id: v.id,
      start: [v.startLng || v.lng, v.startLat || v.lat],
      end: v.returnToStart !== false ? [v.endLng || v.startLng || v.lng, v.endLat || v.startLat || v.lat] : null,
      // Capacity: array de inteiros ou null se nao definido
      capacity: v.capacity != null ? [Math.round(v.capacity)] : null,
      time_window: v.timeWindow || null,
      skills: v.skills || null,
      profile: v.profile || 'car',
    })),
    jobs: jobs.map((j) => clean({
      id: j.id,
      location: [j.lng, j.lat],
      service: j.serviceTime || 300, // 5 minutos padrao
      // Delivery/pickup: array de inteiros ou null
      delivery: j.delivery != null ? [Math.round(j.delivery)] : null,
      pickup: j.pickup != null ? [Math.round(j.pickup)] : null,
      time_windows: j.timeWindows || null,
      skills: j.skills || null,
      priority: j.priority || 0,
    })),
    options: {
      g: true, // Incluir geometria
      ...options,
    },
  };

  // Adicionar shipments apenas se existir
  if (shipments && shipments.length > 0) {
    request.shipments = shipments;
  }


  const response = await fetchWithTimeout(routingConfig.vroom.baseUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    if (response.status === 429) {
      throw new Error('VROOM: Rate limit exceeded — try again later');
    }
    // Tentar obter detalhes do erro do body
    let errorDetail = response.statusText;
    try {
      const errorBody = await response.text();
      errorDetail = errorBody || response.statusText;
    } catch (e) {
      // Ignorar erro ao ler body
    }
    if (response.status >= 500) {
      throw new Error(`VROOM: Server error (${response.status}) — ${errorDetail}`);
    }
    throw new Error(`VROOM: HTTP ${response.status} — ${errorDetail}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(`VROOM optimization failed: ${data.error || 'Unknown error'}`);
  }

  if (!data.routes || data.routes.length === 0) {
    throw new Error('VROOM returned no routes — all jobs may be unassigned');
  }

  return {
    summary: data.summary,
    routes: data.routes.map((r) => ({
      vehicleId: r.vehicle,
      cost: r.cost,
      distance: r.distance,
      duration: r.duration,
      waitingTime: r.waiting_time,
      serviceTime: r.service,
      steps: r.steps.map((s) => ({
        type: s.type,
        id: s.id || s.job || s.shipment,
        location: s.location ? { lng: s.location[0], lat: s.location[1] } : null,
        arrival: s.arrival,
        duration: s.duration,
        distance: s.distance,
        load: s.load,
        waitingTime: s.waiting_time,
      })),
      geometry: r.geometry, // Encoded polyline
    })),
    unassigned: data.unassigned || [],
    computingTimes: data.summary.computing_times,
  };
};

// ============================================================================
// NOMINATIM - Geocoding
// ============================================================================

/**
 * Busca endereco por texto (geocoding)
 * @param {string} query - Texto de busca
 * @param {Object} options - Opcoes de busca
 * @returns {Promise<Array>} Lista de resultados
 */
export const searchAddress = async (query, options = {}) => {
  await loadConfig();

  // Usar searchUrl para busca (servidor diferente do reverse geocoding)
  const searchUrl = routingConfig.nominatim.searchUrl || routingConfig.nominatim.baseUrl;
  if (!searchUrl) {
    throw new Error('Nominatim URL not configured');
  }

  const { limit = 5, countryCode = 'br' } = options;

  const cacheKey = `search:${query}:${limit}:${countryCode}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  const params = new URLSearchParams({
    q: query,
    format: 'json',
    limit: limit.toString(),
    countrycodes: countryCode,
    addressdetails: '1',
  });

  const url = `${searchUrl}/search?${params}`;

  const response = await fetchWithTimeout(url);
  if (!response.ok) {
    handleHttpError('Nominatim search', response);
  }

  const data = await response.json();

  const result = data.map((item) => ({
    id: item.place_id,
    lat: parseFloat(item.lat),
    lng: parseFloat(item.lon),
    displayName: item.display_name,
    type: item.type,
    address: item.address || {},
    importance: item.importance,
  }));

  setCache(cacheKey, result);
  return result;
};

/**
 * Busca endereco por coordenadas (reverse geocoding)
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<Object>} Resultado do endereco
 */
export const reverseGeocode = async (lat, lng) => {
  await loadConfig();

  if (!routingConfig.nominatim.baseUrl) {
    throw new Error('Nominatim URL not configured');
  }

  // Arredondar para ~11m de precisao para melhor cache hit rate
  const rlat = Math.round(lat * 10000) / 10000;
  const rlng = Math.round(lng * 10000) / 10000;
  const cacheKey = `reverse:${rlat}:${rlng}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  const params = new URLSearchParams({
    lat: lat.toString(),
    lon: lng.toString(),
    format: 'json',
    addressdetails: '1',
  });

  const url = `${routingConfig.nominatim.baseUrl}/reverse?${params}`;

  const response = await fetchWithTimeout(url);
  if (!response.ok) {
    handleHttpError('Nominatim reverse', response);
  }

  const data = await response.json();

  if (data.error) {
    throw new Error(`Nominatim reverse failed: ${data.error}`);
  }

  const result = {
    id: data.place_id,
    lat: parseFloat(data.lat),
    lng: parseFloat(data.lon),
    displayName: data.display_name,
    type: data.type,
    address: data.address || {},
  };

  setCache(cacheKey, result);
  return result;
};

// ============================================================================
// UTILS - Funcoes auxiliares
// ============================================================================

/**
 * Decodifica polyline encoded (formato Google/OSRM)
 * @param {string} encoded - Polyline encoded
 * @param {number} precision - Precisao (5 ou 6 digitos)
 * @returns {Array<{lat: number, lng: number}>} Array de coordenadas
 */
export const decodePolyline = (encoded, precision = 5) => {
  const factor = 10 ** precision;
  const coordinates = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let shift = 0;
    let result = 0;
    let byte;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    lat += (result & 1) ? ~(result >> 1) : result >> 1;

    shift = 0;
    result = 0;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    lng += (result & 1) ? ~(result >> 1) : result >> 1;

    coordinates.push({
      lat: lat / factor,
      lng: lng / factor,
    });
  }

  return coordinates;
};

/**
 * Converte polyline encoded para GeoJSON LineString
 * @param {string} encoded - Polyline encoded
 * @returns {Object} GeoJSON LineString
 */
export const polylineToGeoJSON = (encoded) => {
  const coords = decodePolyline(encoded);
  return {
    type: 'LineString',
    coordinates: coords.map((c) => [c.lng, c.lat]),
  };
};

/**
 * Formata distancia para exibicao
 * @param {number} meters - Distancia em metros
 * @returns {string} Distancia formatada
 */
export const formatDistance = (meters) => {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)} km`;
  }
  return `${Math.round(meters)} m`;
};

/**
 * Formata duracao para exibicao
 * @param {number} seconds - Duracao em segundos
 * @returns {string} Duracao formatada
 */
export const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  }
  return `${minutes} min`;
};

/**
 * Formata horario de chegada
 * @param {number} arrivalSeconds - Segundos desde meia-noite
 * @returns {string} Horario formatado (HH:MM)
 */
export const formatArrivalTime = (arrivalSeconds) => {
  const hours = Math.floor(arrivalSeconds / 3600);
  const minutes = Math.floor((arrivalSeconds % 3600) / 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Export default com todos os metodos
export default {
  isRoutingAvailable,
  getRoutingConfig,
  clearConfigCache,
  calculateRoute,
  calculateMatrix,
  optimizeTSP,
  optimizeRoutes,
  searchAddress,
  reverseGeocode,
  decodePolyline,
  polylineToGeoJSON,
  formatDistance,
  formatDuration,
  formatArrivalTime,
};
